import { AppCommandBar } from 'app/AppCommandBar'
import { FlexSpinner } from 'components'
import { ViewAuthorizer } from 'components/AccessDenied'
import { StickyPane } from 'components/StickyPane'
import { ConfirmDeleteDialog } from 'dialogs/ConfirmDeleteDialog'
import { FormikActions } from 'formik'
import {
  SenderRecipieltFormSchema,
  SenderRecipientForm,
  SenderRecipientValues,
} from 'forms/SenderRecipientForm/SenderRecipientForm'
import { omitDeep } from 'helpers'
import {
  useCreateCommand,
  useCustomSearchCommand,
  useDeselectCommand,
  useEditCommand,
} from 'hooks/useCommand'
import { useDialogState } from 'hooks/useDialogState'
import { useFormikPanelState } from 'hooks/useFormikPanelState'
import { useRouteParams } from 'hooks/useRouteParams'
import { useSelection } from 'hooks/useSelection'
import {
  SenderRecipient,
  SenderRecipientInsertType,
  SenderRecipientSetType,
  UserRole,
} from 'model'
import { FormikPanel } from 'panels'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { FormType } from 'views/helpers'
import { AdminSenderRecipientList } from './AdminSenderRecipientList'
import {
  ReadSenderRecipientsQueryVariables,
  useCreateSenderRecipientMutation,
  useDeleteSenderRecipientMutation,
  useReadSenderRecipientsQuery,
  useUpdateSenderRecipientMutation,
} from './AdminSenderRecipientView.generated'

const AdminSenderRecipientListView: React.FC = () => {
  const { t } = useTranslation()

  const [createSenderRecipient] = useCreateSenderRecipientMutation()
  const [updateSenderRecipient] = useUpdateSenderRecipientMutation()
  const [deleteSenderRecipient] = useDeleteSenderRecipientMutation()
  const [formType, setFormType] = React.useState()
  const [selectedItemId, setSelectedItemId] = React.useState()

  const [routeParams, updateRouteParams] = useRouteParams(
    'admin.data.senderRecipients',
    {
      search: undefined,
    }
  )

  const { loading, error, refetch, data } = useReadSenderRecipientsQuery({
    variables: { search: routeParams.search },
  })

  const items =
    data && data.senderRecipients
      ? data.senderRecipients.filter((x) => !x._deleted)
      : []

  const [selection, selectedItems] = useSelection<SenderRecipient>({
    getKey: (item) => item._id!,
  })

  const [panelState, showPanel, hidePanel] = useFormikPanelState()
  const [dialogState, , hideDialog] = useDialogState()

  /** submits */
  const handleSubmit = async (
    values: SenderRecipientValues,
    formikActions: FormikActions<SenderRecipientValues>
  ) => {
    formikActions.setSubmitting(true)
    if (formType === FormType.insert) {
      await createSenderRecipient({
        variables: { input: values as SenderRecipientInsertType },
      })
    } else if (formType === FormType.update) {
      await updateSenderRecipient({
        variables: {
          id: selectedItemId,
          update: values as SenderRecipientSetType,
        },
      })
    } else {
      throw new Error(
        ' Custom: Error while SenderReci[ient create or update. FormType not set.'
      )
    }
    formikActions.setSubmitting(false)
    selection.setAllSelected(false)
    refetch({
      variables: { search: routeParams.search },
    } as ReadSenderRecipientsQueryVariables)
    hidePanel()
  }

  const handleConfirmDelete = async () => {
    if (selectedItems.length > 0) {
      const ids = selectedItems.map((x) => x._id)
      await deleteSenderRecipient({
        variables: { ids },
      })
    }
    refetch({
      variables: { search: routeParams.search },
    } as ReadSenderRecipientsQueryVariables)
    hideDialog()
  }

  /** commands */
  useCustomSearchCommand(
    {
      value: routeParams.search,
      placeholder: t('model.senderRecipient.search'),
      onSearch: (value) => updateRouteParams({ search: value }),
      onClear: () => updateRouteParams({ search: undefined }),
    },
    [t, routeParams, updateRouteParams]
  )

  useCreateCommand(
    'senderRecipient',
    {
      priority: 10,
      onClick: () => {
        setFormType(FormType.insert)
        showPanel({} as SenderRecipientInsertType)
      },
    },
    [setFormType]
  )

  useEditCommand(
    { name: 'senderRecipient', count: selectedItems.length },
    {
      priority: 20,
      onClick: () => {
        setFormType(FormType.update)
        setSelectedItemId(selectedItems[0]._id)
        showPanel(
          omitDeep(selectedItems[0], [
            'id',
            '__typename',
          ]) as SenderRecipientSetType
        )
      },
    },
    [selectedItems, setFormType]
  )

  // useDeleteCommand(
  //   { name: 'senderRecipient', count: selectedItems.length },
  //   {
  //     priority: 30,
  //     onClick: showDialog,
  //   },
  //   [selectedItems]
  // )

  /** farCommands */
  useDeselectCommand(
    selectedItems.length,
    {
      priority: 10,
      farCommand: true,
      onClick: () => selection.setItems([], true),
    },
    [selectedItems]
  )

  return (
    <FlexSpinner loading={loading} error={error}>
      <AdminSenderRecipientList
        items={items}
        selection={selection}
        onItemInvoked={() =>
          selectedItems.length === 1
            ? (() => {
                setFormType(FormType.update)
                setSelectedItemId(selectedItems[0]._id)
                showPanel(
                  omitDeep(selectedItems[0], [
                    'id',
                    '__typename',
                  ]) as SenderRecipientSetType
                )
              })()
            : false
        }
      />
      <FormikPanel
        {...panelState}
        onSubmit={handleSubmit}
        validationSchema={new SenderRecipieltFormSchema()}
        headerText={
          panelState.initialValues && panelState.initialValues._id
            ? t('model.senderRecipient.edit')
            : t('model.senderRecipient.create')
        }
      >
        <SenderRecipientForm />
      </FormikPanel>
      <ConfirmDeleteDialog
        {...dialogState}
        onConfirm={handleConfirmDelete}
        itemCount={selectedItems.length}
      />
    </FlexSpinner>
  )
}

export const AdminSenderRecipientView: React.FC = () => (
  <ViewAuthorizer
    roles={[UserRole.SystemAdministrator, UserRole.ResourceManager]}
  >
    <StickyPane>
      <AppCommandBar />
      <AdminSenderRecipientListView />
    </StickyPane>
  </ViewAuthorizer>
)
